import { useEffect, useState } from "react";
import { Alert, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";

import { Button } from "../../../Helper/Button";
import useRipeningRoom from "../hooks/useRipeningRoom";

export function formatElapsed(ms: number): string {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function useNow(active: boolean) {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (!active) return undefined;
        setNow(Date.now());
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [active]);
    return now;
}

/** Shows how the start of the room's program goes: waiting, or failed with Try again /
 *  Cancel start. Closing it while waiting keeps following the start in the background. */
export default function StartCycleFeedbackModal() {
    const { cycleStartTracker } = useRipeningRoom();
    const {
        cycleStart,
        cycleStartFeedbackOpen,
        closeCycleStartFeedback,
        retryCycleStart,
        retryCycleStartLoading,
        cancelCycleStart,
        cancelCycleStartLoading,
    } = cycleStartTracker ?? {};
    const isOpen = !!cycleStartFeedbackOpen && !!cycleStart && cycleStart.status !== "accepted";
    const pending = cycleStart?.status === "pending";
    const now = useNow(isOpen && pending);
    const busy = !!retryCycleStartLoading || !!cancelCycleStartLoading;

    if (!isOpen || !cycleStart) return null;

    return (
        <Modal isOpen size="md" toggle={busy ? undefined : closeCycleStartFeedback}>
            <ModalHeader toggle={busy ? undefined : closeCycleStartFeedback}>Start Ripening Cycle</ModalHeader>
            <ModalBody>
                {pending ? (
                    <div className="d-flex align-items-center gap-3" data-testid="cycle-start-pending">
                        <Spinner size="sm" color="primary" />
                        <div>
                            <div className="fw-bold">Starting the ripening room…</div>
                            <div className="text-muted small">
                                Waiting for the ripening room ({formatElapsed(now - cycleStart.startedAt)})…
                            </div>
                        </div>
                    </div>
                ) : (
                    <Alert color="danger" className="mb-0" data-testid="cycle-start-failed">
                        {cycleStart.message}
                    </Alert>
                )}
            </ModalBody>
            <ModalFooter>
                {pending ? (
                    <Button color="light" onClick={closeCycleStartFeedback}>Close</Button>
                ) : (
                    <>
                        <Button color="light" onClick={closeCycleStartFeedback} disabled={busy}>Close</Button>
                        <Button color="danger" outline onClick={cancelCycleStart} disabled={busy}>
                            {cancelCycleStartLoading ? "Cancelling…" : "Cancel start"}
                        </Button>
                        <Button color="primary" onClick={retryCycleStart} disabled={busy}>
                            {retryCycleStartLoading ? "Trying again…" : "Try again"}
                        </Button>
                    </>
                )}
            </ModalFooter>
        </Modal>
    );
}
